/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, FC } from "react";
import "./createContent.css";
import Stripo from "../../component/stripo/Stripto";
import CreateContentModal from "./createContentModal";
import { useLocation, useParams } from "react-router-dom";
import XModal from "../../component/modal/XModal";
import useAxios from "../../hooks/useAxios";
import XButton from "../../component/button/XButton";
import { stripoCssTemplate, stripoHtmlTemplate } from "../../component/stripo/stripo.template";
import { RootState } from "../../redux/rootReducer";
import { useSelector } from "react-redux";
import { API_ENDPOINTS } from "../../constants/api";
import { toaster } from "../../utils/toast";

declare global {
  interface Window {
    Stripo: any;
    StripoApi: any;
  }
}
const CreateContent: FC = () => {
  const { orgLocationMap, selectedLocationId } = useSelector((state: RootState) => state.user);

  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [previewCss, setPreviewCss] = useState<string>("");
  const [previewModal, setPreviewModal] = useState<boolean>(false);
  const [html, setHtml] = useState<string>();
  const [css, setCss] = useState<string>();
  const [createContentModal, setCreateContentModal] = useState<boolean>(false);
  const [rssFeedId, setRssFeedId] = useState<string>('');
  const [rssMaxArticles, setRssMaxArticles] = useState<number>(3);
  const { id: templateId } = useParams();
  const location = useLocation();
  const { editTemplateData, preBuildTemplate } = location.state || {};
  const { isLoading, setIsLoading, fetchData, postData } = useAxios();

  useEffect(() => {
    getInitialTemplate();
  }, [selectedLocationId, orgLocationMap]);

  const formatLocationAddress = (loc: any): string => {
    if (!loc) return '';
    const street = (loc.address || '').trim();
    const city = (loc.city_name || loc.city || '').trim();
    const state = (loc.state_code || loc.state_name || loc.state || '').trim();
    const zip = (loc.zipcode || '').trim();

    const stateZip = [state, zip].filter(Boolean).join(' ');
    const cityStateZip = [city, stateZip].filter(Boolean).join(', ');

    if (street && cityStateZip) {
      if (street.toLowerCase().includes(city.toLowerCase()) && (zip ? street.includes(zip) : true)) {
        return street;
      }
      return `${street}, ${cityStateZip}`;
    }
    return street || cityStateZip || '';
  };

  const getInitialTemplate = async () => {
    const location = orgLocationMap?.[+selectedLocationId];
    const locationName = (location?.display_name || '').trim();
    const formattedAddress = formatLocationAddress(location);

    const finalLocationName = locationName || (!formattedAddress ? (location?.address || '').trim() : '');
    const finalAddress = formattedAddress !== finalLocationName ? formattedAddress : '';

    let singleLineLocationHtml = '';
    if (finalLocationName && finalAddress) {
      singleLineLocationHtml = `<p class="footer-text location-info" style="font-size: 12px; line-height: 18px; color: #4b5563; margin: 0 0 8px 0; text-align: center;"><span class="location-name" style="font-weight: 600; color: #1f2937;">${finalLocationName}</span><span class="location-separator" style="color: #9ca3af; padding: 0 6px;">&bull;</span><span class="address" style="color: #6b7280;">${finalAddress}</span></p>`;
    } else if (finalLocationName) {
      singleLineLocationHtml = `<p class="footer-text location-info" style="font-size: 12px; line-height: 18px; color: #4b5563; margin: 0 0 8px 0; text-align: center;"><span class="location-name" style="font-weight: 600; color: #1f2937;">${finalLocationName}</span></p>`;
    } else if (finalAddress) {
      singleLineLocationHtml = `<p class="footer-text location-info" style="font-size: 12px; line-height: 18px; color: #6b7280; margin: 0 0 8px 0; text-align: center;"><span class="address" style="color: #6b7280;">${finalAddress}</span></p>`;
    }
    const currentYear = new Date().getFullYear();

    const updateFooter = (rawHtml: string) => {
      let updated = rawHtml ?? '';

      // Update copyright year
      updated = updated.replace(/&copy;\s*\d{4}\s*Patient Campaign/gi, `&copy; ${currentYear} Patient Campaign`);

      // 1. If location-info already exists:
      if (/<p[^>]*class=["'][^"']*\blocation-info\b[^"']*["'][^>]*>.*?<\/p>/is.test(updated)) {
        updated = updated.replace(
          /<p[^>]*class=["'][^"']*\blocation-info\b[^"']*["'][^>]*>.*?<\/p>/is,
          singleLineLocationHtml
        );
      }
      // 2. If adjacent location-name and address tags exist:
      else if (/<p[^>]*class=["'][^"']*\blocation-name\b[^"']*["'][^>]*>.*?<\/p>\s*<p[^>]*class=["'][^"']*\baddress\b[^"']*["'][^>]*>.*?<\/p>/is.test(updated)) {
        updated = updated.replace(
          /<p[^>]*class=["'][^"']*\blocation-name\b[^"']*["'][^>]*>.*?<\/p>\s*<p[^>]*class=["'][^"']*\baddress\b[^"']*["'][^>]*>.*?<\/p>/is,
          singleLineLocationHtml
        );
      }
      // 3. If reverse adjacent address and location-name tags exist:
      else if (/<p[^>]*class=["'][^"']*\baddress\b[^"']*["'][^>]*>.*?<\/p>\s*<p[^>]*class=["'][^"']*\blocation-name\b[^"']*["'][^>]*>.*?<\/p>/is.test(updated)) {
        updated = updated.replace(
          /<p[^>]*class=["'][^"']*\baddress\b[^"']*["'][^>]*>.*?<\/p>\s*<p[^>]*class=["'][^"']*\blocation-name\b[^"']*["'][^>]*>.*?<\/p>/is,
          singleLineLocationHtml
        );
      }
      // 4. If both exist non-adjacently:
      else if (
        /<p[^>]*class=["'][^"']*\blocation-name\b[^"']*["'][^>]*>.*?<\/p>/is.test(updated) &&
        /<p[^>]*class=["'][^"']*\baddress\b[^"']*["'][^>]*>.*?<\/p>/is.test(updated)
      ) {
        updated = updated
          .replace(/<p[^>]*class=["'][^"']*\blocation-name\b[^"']*["'][^>]*>.*?<\/p>/is, singleLineLocationHtml)
          .replace(/<p[^>]*class=["'][^"']*\baddress\b[^"']*["'][^>]*>.*?<\/p>/is, '');
      }
      // 5. Only location-name exists:
      else if (/<p[^>]*class=["'][^"']*\blocation-name\b[^"']*["'][^>]*>.*?<\/p>/is.test(updated)) {
        updated = updated.replace(
          /<p[^>]*class=["'][^"']*\blocation-name\b[^"']*["'][^>]*>.*?<\/p>/is,
          singleLineLocationHtml
        );
      }
      // 6. Only address exists:
      else if (/<p[^>]*class=["'][^"']*\baddress\b[^"']*["'][^>]*>.*?<\/p>/is.test(updated)) {
        updated = updated.replace(
          /<p[^>]*class=["'][^"']*\baddress\b[^"']*["'][^>]*>.*?<\/p>/is,
          singleLineLocationHtml
        );
      }
      // 7. If template-footer exists but has no location tag:
      else if (/<footer[^>]*class=["'][^"']*\btemplate-footer\b[^"']*["'][^>]*>/is.test(updated)) {
        if (singleLineLocationHtml) {
          if (/<footer[^>]*class=["'][^"']*\btemplate-footer\b[^"']*["'][^>]*>\s*<p/is.test(updated)) {
            updated = updated.replace(
              /(<footer[^>]*class=["'][^"']*\btemplate-footer\b[^"']*["'][^>]*>\s*)(<p)/is,
              `$1${singleLineLocationHtml}\n$2`
            );
          } else {
            updated = updated.replace(/(<\/footer>)/i, `${singleLineLocationHtml}\n$1`);
          }
        }
      }

      return updated;
    };

    let h, c;
    if (templateId) {
      let templateData = editTemplateData;
      if (!templateData) {
        // navigation state (e.g. from a page refresh or direct link) is gone, fetch it instead
        const response = await fetchData(`${API_ENDPOINTS.GET_TEMPLATE_BY_ID}/${templateId}`);
        templateData = response?.data;
      }
      h = updateFooter(templateData?.html ?? '');
      c = templateData?.css;
      const initialFeedId = templateData?.rss_feed_id ? String(templateData.rss_feed_id) : '';
      setRssFeedId(initialFeedId);
      const rawHtml = templateData?.html || templateData?.content || '';
      const match = rawHtml.match(/data-rss-max-items=["']?(\d+)["']?/i);
      if (match && match[1]) {
        setRssMaxArticles(parseInt(match[1], 10));
      } else if (initialFeedId) {
        const selectedFeed = location?.rss_feeds?.find((f: any) => String(f.id) === String(initialFeedId));
        if (selectedFeed?.max_items) {
          setRssMaxArticles(Math.min(10, Math.max(1, Number(selectedFeed.max_items))));
        } else {
          setRssMaxArticles(3);
        }
      } else {
        setRssMaxArticles(3);
      }
    } else {
      h = updateFooter(preBuildTemplate?.html || stripoHtmlTemplate);
      c = preBuildTemplate?.css || stripoCssTemplate;
      setRssFeedId('');
      setRssMaxArticles(3);
    }
    setHtml(h);
    setCss(c);
  };

  const handleRssFeedChange = (newFeedId: string) => {
    setRssFeedId(newFeedId);
    if (!newFeedId) {
      setRssMaxArticles(3);
      return;
    }
    const location = orgLocationMap?.[+selectedLocationId];
    const feed = location?.rss_feeds?.find((f: any) => String(f.id) === String(newFeedId));
    if (feed?.max_items) {
      setRssMaxArticles(Math.min(10, Math.max(1, Number(feed.max_items))));
    } else {
      setRssMaxArticles(3);
    }
  };

  // RSS markers are resolved server-side so preview and send share the same
  // layout (auto digest and/or nested {{#rss_items}} / {{#rss_inner}} blocks).
  const RSS_TAG_PATTERN = /\{\{[#/]?rss_[a-zA-Z_]+\}\}/i;

  const applyRssPreview = async (templateHtml: string, templateCss: string) => {
    let finalHtml = templateHtml;

    if (RSS_TAG_PATTERN.test(templateHtml)) {
      const response = await postData(API_ENDPOINTS.LOCATION_RSS_PREVIEW, {
        content: templateHtml,
        rssFeedId,
        maxItems: rssMaxArticles,
      });
      if (response?.success && response.data?.html) {
        finalHtml = response.data.html;
      } else {
        toaster({ message: response?.message || "Couldn't fetch the RSS feed for preview", success: false });
      }
    }

    setPreviewHtml(finalHtml);
    setPreviewCss(templateCss);
    setPreviewModal(true);
    setIsLoading(false);
  };

  const handlePreview = () => {
    setIsLoading(true);
    // compileEmail is the HTML we save/send. getTemplate still has editor
    // chrome and can wrap merge tags so Start/End loops never match.
    window.StripoApi.compileEmail((err: string, compiledHtml: string) => {
      if (!err && compiledHtml) {
        applyRssPreview(compiledHtml, "");
        return;
      }
      window.StripoApi.getTemplate((templateHtml: string, templateCss: string) => {
        applyRssPreview(templateHtml, templateCss);
      });
    });
  };

  return (
    <div>
      <Stripo
        html={html}
        css={css}
        rssFeedId={rssFeedId}
        onRssFeedChange={handleRssFeedChange}
        rssMaxArticles={rssMaxArticles}
        onRssMaxArticlesChange={setRssMaxArticles}
      />
      {previewModal && (
        <XModal
          title="Preview"
          confirmText="Next"
          show={previewModal}
          handleClose={() => setPreviewModal(false)}
          handleConfirm={() => {
            setPreviewModal(false);
            setCreateContentModal(true);
          }}
          size="xl"
          body={
            <div style={{ maxHeight: "67vh", overflowY: "auto" }}>
              <style>{`${previewCss}`}</style>
              <div
                style={{ pointerEvents: 'none' }}
                dangerouslySetInnerHTML={{
                  __html: previewHtml ?? "",
                }}
              />
            </div>
          }
        />
      )}
      {createContentModal && (
        <CreateContentModal
          visible={createContentModal}
          setVisible={setCreateContentModal}
          editTemplateData={editTemplateData}
          previewHtml={previewHtml}
          previewCss={previewCss}
          rssFeedId={rssFeedId}
          rssMaxArticles={rssMaxArticles}
        />
      )}
      <div className="d-flex justify-content-end align-items-center gap-2">
        <XButton
          buttonText="Preview"
          loading={isLoading}
          onClick={handlePreview}
        />
      </div>
    </div>
  );
};

export default CreateContent;
